// Curvas de crecimiento por edad (Serie 028) — factory genérico, puro, sin
// React/recharts, testeable con Vitest sin DOM (mismo patrón que
// paciente/curvaTermica.ts). Generalizado en la Sub-tanda B a partir del
// primer caso real (peso/edad, Sub-tanda A) — mismo criterio que
// `crearFormularioCurvaCrecimiento` (formulario/discretos/curvaCrecimiento.tsx):
// un solo factory configurado por indicador, no un componente por indicador.
//
// Corrección a un supuesto de Fase 1 (ya documentada en la Sub-tanda A): las
// líneas SD NO son una `BandaReferencia` (rectángulo plano) — varían con la
// edad, así que cada una es una `Serie` más (gris, punteada).
import type { Observation, Patient } from '@medplum/fhirtypes';
import { PALETA_SERIES } from '../graficos/tema';
import type { PuntoSerie, Serie } from '../graficos/tipos';
import { ajustarTallaPorPosicion, comoFilasLms, edadCorregida, edadExactaEnDias, interpolarLms, valorParaZ, type FilaLmsTupla } from '../lib/percentilesCrecimiento';
import datosImc0a5 from '../graficos/datos/imcEdad0a5Oms.json';
import datosImc5a19 from '../graficos/datos/imcEdad5a19Oms.json';
import datosLongitudFenton from '../graficos/datos/longitudFentonOms.json';
import datosPerimetroCefalico from '../graficos/datos/perimetroCefalicoEdadOms.json';
import datosPerimetroCefalicoFenton from '../graficos/datos/perimetroCefalicoFentonOms.json';
import datosPeso5a10 from '../graficos/datos/pesoEdad5a10Oms.json';
import datosPesoNchs10a19 from '../graficos/datos/pesoEdad10a19Nchs.json';
import datosPesoEdad from '../graficos/datos/pesoEdadOms.json';
import datosPesoFenton from '../graficos/datos/pesoFentonOms.json';
import datosPesoLongitud from '../graficos/datos/pesoLongitudOms.json';
import datosPesoTalla from '../graficos/datos/pesoTallaOms.json';
import datosTalla5a19 from '../graficos/datos/tallaEdad5a19Oms.json';
import datosTallaEdad from '../graficos/datos/tallaEdadOms.json';

/** Una línea de referencia de la curva: el z-score al que se evalúa L/M/S, con su id y rótulo. */
export interface LineaReferencia {
  readonly z: number;
  readonly id: string;
  readonly etiqueta: string;
}

/** Líneas oficiales OMS/MSP — desviaciones estándar, no percentiles (ver PLAN.md). */
const LINEAS_SD: readonly LineaReferencia[] = ([-3, -2, -1, 0, 1, 2, 3] as const).map((z) => ({ z, id: `sd${z}`, etiqueta: etiquetaSd(z) }));

/** Percentiles del papel (056A/056B, Peso/Edad NCHS/2000): P3, P10, P25, P50, P75, P90, P97 como z-score de la normal. */
export const LINEAS_PERCENTILES: readonly LineaReferencia[] = [
  { z: -1.880794, id: 'p3', etiqueta: 'P3' },
  { z: -1.281552, id: 'p10', etiqueta: 'P10' },
  { z: -0.67449, id: 'p25', etiqueta: 'P25' },
  { z: 0, id: 'p50', etiqueta: 'P50' },
  { z: 0.67449, id: 'p75', etiqueta: 'P75' },
  { z: 1.281552, id: 'p90', etiqueta: 'P90' },
  { z: 1.880794, id: 'p97', etiqueta: 'P97' },
];
/** Resolución de muestreo de las líneas SD — cada 15 días alcanza para una curva visualmente suave sin mandar todas las filas del dataset por línea. */
const PASO_MUESTREO_DIAS = 15;
/** Días por mes medio (365.25 / 12). */
const DIAS_POR_MES = 30.4375;
const COLOR_LINEA_SD = 'var(--color-hce-muted)';

function etiquetaSd(z: number): string {
  if (z === 0) return 'Mediana';
  return `${z > 0 ? '+' : ''}${z} DE`;
}

function lineaSd(dataset: readonly FilaLmsTupla[], linea: LineaReferencia, edadMinDias: number, edadMaxDias: number, paso: number = PASO_MUESTREO_DIAS): Serie {
  // OJO: `edadMinDias`/`edadMaxDias`/`paso` están en la unidad del eje X del dataset: días (casi todos) o cm (peso para la longitud/talla).
  const { z } = linea;
  const puntos: PuntoSerie[] = [];
  for (let dia = edadMinDias; dia < edadMaxDias; dia += paso) {
    const lms = interpolarLms(dataset, dia);
    if (lms) puntos.push({ x: dia, y: valorParaZ(lms, z) });
  }
  const lmsFinal = interpolarLms(dataset, edadMaxDias);
  if (lmsFinal) puntos.push({ x: edadMaxDias, y: valorParaZ(lmsFinal, z) });
  return { id: linea.id, etiqueta: linea.etiqueta, color: COLOR_LINEA_SD, trazo: 'punteado', puntos };
}

export interface CurvaPercentilDatos {
  readonly serieReal: Serie;
  readonly lineasSd: readonly Serie[];
}

/** Arma serieReal + líneas SD a partir de puntos {x,y} YA resueltos (edad en días, valor ya ajustado) — compartido entre las curvas por-LOINC y la de IMC (que empareja 2 LOINC en vez de filtrar uno solo). */
function armarCurvaDesdePuntos(
  puntos: readonly PuntoSerie[],
  dataset: readonly FilaLmsTupla[],
  idSerie: string,
  etiquetaSerie: string,
  unidad: string,
  lineas: readonly LineaReferencia[] = LINEAS_SD,
  paso: number = PASO_MUESTREO_DIAS,
): CurvaPercentilDatos | undefined {
  if (puntos.length < 2) return undefined;
  const edadMinDias = puntos[0].x;
  const edadMaxDias = puntos[puntos.length - 1].x;
  const { color, trazo } = PALETA_SERIES[1];
  return {
    lineasSd: lineas.map((linea) => lineaSd(dataset, linea, edadMinDias, edadMaxDias, paso)),
    serieReal: { id: idSerie, etiqueta: etiquetaSerie, color, trazo, unidad, puntos },
  };
}

export interface RangoEdadDias {
  readonly min: number;
  readonly max: number;
}

export interface ConfigCurvaPercentil {
  readonly loinc: string;
  readonly datasetBoys: readonly FilaLmsTupla[];
  readonly datasetGirls: readonly FilaLmsTupla[];
  readonly idSerie: string;
  readonly etiquetaSerie: string;
  readonly unidad: string;
  /** Solo talla (ver Riesgo 2, PLAN.md): ajusta el valor crudo (acostado/de pie) antes de graficar — recibe la Observation completa para leer el `component` de posición. Recibe la edad YA corregida si `ajustarEdad` corrió antes. `undefined` = sin ajuste (peso, perímetro cefálico). */
  readonly ajustarValor?: (valorCrudo: number, edadDias: number, observation: Observation) => number;
  /** Solo 028B (ver Riesgo 9, PLAN.md): reemplaza la edad cronológica por la edad corregida si la Observation trae el `component` de edad gestacional al nacer y el paciente es prematuro. `undefined` = sin ajuste (edad cronológica tal cual, todo lo demás de la Serie 028). */
  readonly ajustarEdad?: (edadCronologicaDias: number, observation: Observation) => number;
  /** Excluye mediciones fuera de este rango etario — para que 028B (0-5a) y 028C (5-9a/5-19a impreso) no mezclen los mismos LOINC de un paciente con historial en ambos rangos. `undefined` = sin tope (compatibilidad hacia atrás). */
  readonly rangoEdadDias?: RangoEdadDias;
  /** Líneas de referencia a dibujar. `undefined` = las 7 desviaciones estándar de la OMS; 056A/056B (Peso/Edad NCHS) usan los percentiles del papel. */
  readonly lineas?: readonly LineaReferencia[];
}

/**
 * Mediciones del paciente de una curva por-LOINC como puntos {x: edad en días, y: valor ya ajustado}, ordenadas por edad y SIN mínimo de puntos —
 * el PDF firmado (`curvasControlNinoPdf.ts`) dibuja también una sola medición. `[]` si falta la fecha de nacimiento o el sexo no es male/female.
 */
export function puntosPacienteCurva(config: ConfigCurvaPercentil, observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): PuntoSerie[] {
  if (!patient.birthDate) return [];
  if (patient.gender !== 'male' && patient.gender !== 'female') return [];
  const nacimiento = patient.birthDate;

  return observaciones
    .filter((o) => o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === config.loinc))
    .filter((o): o is Observation & { effectiveDateTime: string; valueQuantity: { value: number } } => typeof o.effectiveDateTime === 'string' && typeof o.valueQuantity?.value === 'number')
    .map((o) => {
      const edadCronologica = edadExactaEnDias(nacimiento, o.effectiveDateTime);
      const edadDias = config.ajustarEdad ? config.ajustarEdad(edadCronologica, o) : edadCronologica;
      const valor = config.ajustarValor ? config.ajustarValor(o.valueQuantity.value, edadDias, o) : o.valueQuantity.value;
      return { x: edadDias, y: valor };
    })
    .filter((p) => p.x >= 0 && (!config.rangoEdadDias || (p.x >= config.rangoEdadDias.min && p.x <= config.rangoEdadDias.max)))
    .sort((a, b) => a.x - b.x);
}

/**
 * `undefined` si falta fecha de nacimiento, el sexo no es male/female (las
 * tablas OMS solo existen para esos dos), o hay menos de 2 mediciones válidas
 * dentro del rango etario — un solo punto no tiene sentido clínico como "curva".
 */
export function crearCurvaPercentilPorEdad(config: ConfigCurvaPercentil): (observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>) => CurvaPercentilDatos | undefined {
  return function curvaDe(observaciones, patient) {
    if (!patient.birthDate) return undefined;
    if (patient.gender !== 'male' && patient.gender !== 'female') return undefined;
    const puntos = puntosPacienteCurva(config, observaciones, patient);
    const dataset = patient.gender === 'male' ? config.datasetBoys : config.datasetGirls;
    return armarCurvaDesdePuntos(puntos, dataset, config.idSerie, config.etiquetaSerie, config.unidad, config.lineas);
  };
}

export const PESO_LOINC = '29463-7';
export const TALLA_LOINC = '8302-2';
export const PERIMETRO_CEFALICO_LOINC = '9843-4';
/** Mismo LOINC que ya usa Formulario034.tsx (peso gestacional de la madre) — acá en cambio es la edad gestacional AL NACER del bebé, un dato que hasta esta sub-tanda no existía en ningún lugar del código (ver PLAN.md Riesgo 9). */
const EDAD_GESTACIONAL_LOINC = '49051-6';

/** Nombre del `component.code.text` donde `Formulario028B{1,2}` guarda si la talla se midió acostado o de pie — ver formulario/discretos/curvaCrecimiento.tsx. */
export const CODIGO_COMPONENT_POSICION_TALLA = 'Posición de medición';

/** Lee la edad gestacional al nacer (semanas) de una Observation, si `crearFormularioCurvaCrecimiento` la guardó como `component` (LOINC 49051-6) — ver Riesgo 9, PLAN.md. */
function edadGestacionalAlNacerDe(observation: Observation): number | undefined {
  return observation.component?.find((c) => c.code?.coding?.some((co) => co.system === 'http://loinc.org' && co.code === EDAD_GESTACIONAL_LOINC))?.valueQuantity?.value;
}

/** `ajustarEdad` de 028B: si la Observation trae la edad gestacional al nacer, reemplaza edad cronológica por edad corregida (sin cambio si el dato no está o el paciente ya no es prematuro/pasó los 24 meses). */
function ajusteEdadCorregidaSiPrematuro(edadCronologicaDias: number, observation: Observation): number {
  const semanas = edadGestacionalAlNacerDe(observation);
  if (semanas === undefined) return edadCronologicaDias;
  return edadCorregida(edadCronologicaDias, semanas);
}

/** OMS 0-5 años (0-1856 días) — 028B-1/028B-2. */
const RANGO_0A5 = { min: 0, max: 1856 };
/** OMS 5-19 años impresos (talla/IMC) — 028C-1/028C-2. Arranca justo donde termina el 0-5 (1856d) para no dejar un hueco ni superponer. */
const RANGO_5A19 = { min: 1856, max: 1856 + 365.25 * 17 };
/** Tope real de la OMS para peso/edad — no publica más allá de los 10 años (variabilidad puberal). */
const RANGO_5A10_PESO = { min: 1856, max: 1856 + 365.25 * 8 };

function ajusteTallaPosicion(valorCrudo: number, edadDias: number, observation: Observation): number {
  const posicion = observation.component?.find((c) => c.code?.text === CODIGO_COMPONENT_POSICION_TALLA)?.valueString;
  if (posicion !== 'acostado' && posicion !== 'de-pie') return valorCrudo;
  return ajustarTallaPorPosicion(valorCrudo, edadDias, posicion);
}

export const CONFIG_PESO_EDAD_0A5: ConfigCurvaPercentil = {
  loinc: PESO_LOINC,
  datasetBoys: comoFilasLms(datosPesoEdad.boys),
  datasetGirls: comoFilasLms(datosPesoEdad.girls),
  idSerie: 'peso-real',
  etiquetaSerie: 'Peso',
  unidad: 'kg',
  ajustarEdad: ajusteEdadCorregidaSiPrematuro,
  rangoEdadDias: RANGO_0A5,
};
export const curvaPesoEdadDe = crearCurvaPercentilPorEdad(CONFIG_PESO_EDAD_0A5);

export const CONFIG_TALLA_EDAD_0A5: ConfigCurvaPercentil = {
  loinc: TALLA_LOINC,
  datasetBoys: comoFilasLms(datosTallaEdad.boys),
  datasetGirls: comoFilasLms(datosTallaEdad.girls),
  idSerie: 'talla-real',
  etiquetaSerie: 'Talla',
  unidad: 'cm',
  ajustarValor: ajusteTallaPosicion,
  ajustarEdad: ajusteEdadCorregidaSiPrematuro,
  rangoEdadDias: RANGO_0A5,
};
export const curvaTallaEdadDe = crearCurvaPercentilPorEdad(CONFIG_TALLA_EDAD_0A5);

export const CONFIG_PC_EDAD_0A5: ConfigCurvaPercentil = {
  loinc: PERIMETRO_CEFALICO_LOINC,
  datasetBoys: comoFilasLms(datosPerimetroCefalico.boys),
  datasetGirls: comoFilasLms(datosPerimetroCefalico.girls),
  idSerie: 'perimetro-cefalico-real',
  etiquetaSerie: 'Perímetro cefálico',
  unidad: 'cm',
  rangoEdadDias: RANGO_0A5,
};
export const curvaPerimetroCefalicoEdadDe = crearCurvaPercentilPorEdad(CONFIG_PC_EDAD_0A5);

export const CONFIG_TALLA_EDAD_5A19: ConfigCurvaPercentil = {
  loinc: TALLA_LOINC,
  datasetBoys: comoFilasLms(datosTalla5a19.boys),
  datasetGirls: comoFilasLms(datosTalla5a19.girls),
  idSerie: 'talla-real-5a19',
  etiquetaSerie: 'Talla',
  unidad: 'cm',
  rangoEdadDias: RANGO_5A19,
};
export const curvaTallaEdad5a19De = crearCurvaPercentilPorEdad(CONFIG_TALLA_EDAD_5A19);

export const CONFIG_PESO_EDAD_5A10: ConfigCurvaPercentil = {
  loinc: PESO_LOINC,
  datasetBoys: comoFilasLms(datosPeso5a10.boys),
  datasetGirls: comoFilasLms(datosPeso5a10.girls),
  idSerie: 'peso-real-5a10',
  etiquetaSerie: 'Peso',
  unidad: 'kg',
  rangoEdadDias: RANGO_5A10_PESO,
};
export const curvaPesoEdad5a10De = crearCurvaPercentilPorEdad(CONFIG_PESO_EDAD_5A10);

/** 10 a 19 años: lo que imprimen las láminas de 056A/056B (Peso/Edad, Talla/Edad e IMC/Edad). */
const RANGO_10A19 = { min: 365.25 * 10, max: 365.25 * 19 };

/** Talla/Edad OMS 2007 de la lámina de Adolescente (056A/056B): la misma tabla 5-19 años, solo las mediciones de 10 a 19 años. */
export const curvaTallaEdad10a19De = crearCurvaPercentilPorEdad({
  loinc: TALLA_LOINC,
  datasetBoys: comoFilasLms(datosTalla5a19.boys),
  datasetGirls: comoFilasLms(datosTalla5a19.girls),
  idSerie: 'talla-real-10a19',
  etiquetaSerie: 'Talla',
  unidad: 'cm',
  rangoEdadDias: RANGO_10A19,
});

/** IMC/Edad OMS 2007 de la lámina de Adolescente (056A/056B): solo pares peso/talla de 10 a 19 años. */
export function curvaImcEdad10a19De(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): CurvaPercentilDatos | undefined {
  return curvaImcEdadDe(observaciones, patient, RANGO_10A19);
}

/**
 * Peso/Edad 10 a 19 años, PERCENTILES NCHS/2000 (CDC) — la lámina de Adolescente Mujer/Hombre (056A/056B) imprime esta curva,
 * a diferencia de la OMS, que no publica peso/edad después de los 10 años. Datos de graficos/datos/pesoEdad10a19Nchs.json.
 */
export const curvaPesoEdadNchs10a19De = crearCurvaPercentilPorEdad({
  loinc: PESO_LOINC,
  datasetBoys: comoFilasLms(datosPesoNchs10a19.boys),
  datasetGirls: comoFilasLms(datosPesoNchs10a19.girls),
  idSerie: 'peso-real-nchs-10a19',
  etiquetaSerie: 'Peso',
  unidad: 'kg',
  rangoEdadDias: RANGO_10A19,
  lineas: LINEAS_PERCENTILES,
});

/**
 * Las líneas de referencia de una curva de la lámina de adolescentes (056A/056B) sobre TODO el rango de 10 a 19 años (no solo el de las mediciones
 * del paciente), muestreadas cada 120 días: Peso/Edad = 7 percentiles NCHS/2000; Talla/Edad e IMC/Edad = 7 desviaciones estándar OMS 2007. Para el PDF firmado.
 */
export function lineasReferenciaAdolescente(gender: Patient['gender'], tipo: 'peso' | 'talla' | 'imc'): Serie[] {
  if (gender !== 'male' && gender !== 'female') return [];
  const h = gender === 'male';
  const dataset = comoFilasLms(tipo === 'peso' ? (h ? datosPesoNchs10a19.boys : datosPesoNchs10a19.girls) : tipo === 'talla' ? (h ? datosTalla5a19.boys : datosTalla5a19.girls) : h ? datosImc5a19.boys : datosImc5a19.girls);
  return (tipo === 'peso' ? LINEAS_PERCENTILES : LINEAS_SD).map((linea) => lineaSd(dataset, linea, RANGO_10A19.min, RANGO_10A19.max, 120));
}

/** Ajustes opcionales del emparejamiento peso/talla: la edad corregida y la posición de la talla solo aplican a 0-5 años (028B); 028C (5-19 años) no los usa. */
export interface OpcionesParesPesoTalla {
  /** Reemplaza la edad cronológica (p. ej. por la corregida de un prematuro); recibe la Observation de peso del par. */
  readonly ajustarEdad?: (edadCronologicaDias: number, observation: Observation) => number;
  /** Ajusta la talla cruda (acostado/de pie) antes de usarla; recibe la edad YA ajustada y la Observation de talla del par. */
  readonly ajustarTalla?: (tallaCm: number, edadDias: number, observation: Observation) => number;
}

/** Un par peso/talla tomado en el MISMO instante, con la edad ya ajustada: base de IMC/edad y de peso para la longitud/talla. */
interface ParPesoTalla {
  readonly edadDias: number;
  readonly pesoKg: number;
  readonly tallaCm: number;
}

/**
 * Empareja cada Observation de peso con la de talla del MISMO `effectiveDateTime` exacto (confiable porque `crearFormularioCurvaCrecimiento` guarda ambas con
 * el mismo timestamp en un solo submit), dentro de `rango` de edad (ya ajustada). Sin mínimo de puntos. `[]` si falta la fecha de nacimiento o el sexo.
 */
function paresPesoTalla(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>, rango: RangoEdadDias, opciones: OpcionesParesPesoTalla = {}): ParPesoTalla[] {
  if (!patient.birthDate) return [];
  if (patient.gender !== 'male' && patient.gender !== 'female') return [];
  const nacimiento = patient.birthDate;

  const pesoPorFecha = new Map<string, Observation>();
  const tallaPorFecha = new Map<string, Observation>();
  for (const o of observaciones) {
    if (typeof o.effectiveDateTime !== 'string' || typeof o.valueQuantity?.value !== 'number') continue;
    if (o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === PESO_LOINC)) pesoPorFecha.set(o.effectiveDateTime, o);
    if (o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === TALLA_LOINC)) tallaPorFecha.set(o.effectiveDateTime, o);
  }

  const pares: ParPesoTalla[] = [];
  for (const [fecha, obsPeso] of pesoPorFecha) {
    const obsTalla = tallaPorFecha.get(fecha);
    const tallaCruda = obsTalla?.valueQuantity?.value;
    const pesoKg = obsPeso.valueQuantity?.value;
    if (obsTalla === undefined || tallaCruda === undefined || pesoKg === undefined || tallaCruda <= 0) continue;
    const edadCronologica = edadExactaEnDias(nacimiento, fecha);
    const edadDias = opciones.ajustarEdad ? opciones.ajustarEdad(edadCronologica, obsPeso) : edadCronologica;
    if (edadDias < rango.min || edadDias > rango.max) continue;
    pares.push({ edadDias, pesoKg, tallaCm: opciones.ajustarTalla ? opciones.ajustarTalla(tallaCruda, edadDias, obsTalla) : tallaCruda });
  }
  return pares.sort((a, b) => a.edadDias - b.edadDias);
}

/**
 * IMC/edad (028C-1/028C-2) — el IMC nunca se guarda como Observation propia
 * (mismo criterio que `paciente/vitalesResumen.ts:imcCalculado`, ya usado
 * para el tile del dashboard): se empareja cada Observation de peso con la
 * de talla del MISMO `effectiveDateTime` exacto y se calcula `peso / (talla/100)²`
 * por cada par dentro del rango etario 5-19 años.
 */
export function curvaImcEdadDe(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>, rango: RangoEdadDias = RANGO_5A19): CurvaPercentilDatos | undefined {
  if (!patient.birthDate) return undefined;
  if (patient.gender !== 'male' && patient.gender !== 'female') return undefined;
  const puntos = puntosImcEdad(observaciones, patient, rango);
  const dataset = patient.gender === 'male' ? comoFilasLms(datosImc5a19.boys) : comoFilasLms(datosImc5a19.girls);
  return armarCurvaDesdePuntos(puntos, dataset, 'imc-real', 'IMC', 'kg/m²');
}

/** Puntos {x: edad en días, y: IMC} de los pares peso/talla del MISMO instante dentro de `rango`, sin mínimo de puntos (el PDF firmado dibuja también uno solo). `[]` si falta la fecha de nacimiento o el sexo. */
export function puntosImcEdad(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>, rango: RangoEdadDias = RANGO_5A19, opciones?: OpcionesParesPesoTalla): PuntoSerie[] {
  return paresPesoTalla(observaciones, patient, rango, opciones).map((p) => {
    const tallaM = p.tallaCm / 100;
    return { x: p.edadDias, y: p.pesoKg / (tallaM * tallaM) };
  });
}

/** Ajustes de 028B para el IMC/edad y el peso para la longitud/talla de 0 a 5 años: edad corregida si nació prematuro y talla según la posición de medición. */
const OPCIONES_PARES_0A5: OpcionesParesPesoTalla = { ajustarEdad: ajusteEdadCorregidaSiPrematuro, ajustarTalla: ajusteTallaPosicion };

/** IMC/Edad OMS 2006 de 0 a 5 años (028B-1/028B-2): mismos pares peso/talla que 028C, con la edad corregida y la talla por posición de 028B. */
export function puntosImcEdad0a5(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): PuntoSerie[] {
  return puntosImcEdad(observaciones, patient, RANGO_0A5, OPCIONES_PARES_0A5);
}

export function curvaImcEdad0a5De(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): CurvaPercentilDatos | undefined {
  if (patient.gender !== 'male' && patient.gender !== 'female') return undefined;
  const dataset = comoFilasLms(patient.gender === 'male' ? datosImc0a5.boys : datosImc0a5.girls);
  return armarCurvaDesdePuntos(puntosImcEdad0a5(observaciones, patient), dataset, 'imc-real-0a5', 'IMC', 'kg/m²');
}

/**
 * Peso para la longitud (0 a 2 años, 45 a 110 cm) y peso para la talla (2 a 5 años, 65 a 120 cm) — OMS 2006. A diferencia del resto, el eje X NO es la edad
 * sino la longitud/talla (cm): el dataset y las líneas SD están indexados por cm, en pasos de 0,1. Se usa la longitud (acostado) antes de los 24 meses y la
 * talla (de pie) desde los 24 (mismo corte y mismo ajuste de posición que Talla/Edad); el rango etario decide cuál de las dos tablas corresponde.
 */
interface CurvaPesoParaTalla {
  readonly datos: { readonly boys: readonly (readonly number[])[]; readonly girls: readonly (readonly number[])[] };
  /** Rango de la longitud/talla (cm) que cubre la tabla oficial. */
  readonly rangoCm: RangoEdadDias;
  /** Rango de edad (días) al que corresponde la tabla. */
  readonly rangoEdadDias: RangoEdadDias;
  readonly idSerie: string;
}

/** Las líneas SD del peso para la longitud/talla se muestrean cada 1 cm (la tabla está cada 0,1): 66 puntos, bajo el tope de 80 del PDF. */
const PASO_MUESTREO_CM = 1;
/** 24 meses = 730,5 días: la OMS cambia de tabla ahí; el borde pertenece a peso para la talla (2 años cumplidos). */
const DIAS_24_MESES = 24 * DIAS_POR_MES;

const PESO_PARA_LONGITUD: CurvaPesoParaTalla = { datos: datosPesoLongitud, rangoCm: { min: 45, max: 110 }, rangoEdadDias: { min: 0, max: DIAS_24_MESES - 1e-9 }, idSerie: 'peso-longitud-real' };
const PESO_PARA_TALLA: CurvaPesoParaTalla = { datos: datosPesoTalla, rangoCm: { min: 65, max: 120 }, rangoEdadDias: { min: DIAS_24_MESES, max: 60 * DIAS_POR_MES }, idSerie: 'peso-talla-real' };

/** Puntos {x: longitud/talla en cm, y: peso en kg} de los pares del mismo instante que caen en la tabla (edad y cm). */
function puntosPesoParaTalla(c: CurvaPesoParaTalla, observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): PuntoSerie[] {
  return paresPesoTalla(observaciones, patient, c.rangoEdadDias, OPCIONES_PARES_0A5)
    .filter((p) => p.tallaCm >= c.rangoCm.min && p.tallaCm <= c.rangoCm.max)
    .map((p) => ({ x: p.tallaCm, y: p.pesoKg }))
    .sort((a, b) => a.x - b.x);
}

function curvaPesoParaTallaDe(c: CurvaPesoParaTalla, observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): CurvaPercentilDatos | undefined {
  if (patient.gender !== 'male' && patient.gender !== 'female') return undefined;
  const dataset = comoFilasLms(patient.gender === 'male' ? c.datos.boys : c.datos.girls);
  const puntos = puntosPesoParaTalla(c, observaciones, patient);
  // Dos visitas con la MISMA talla no son una curva: el eje X (cm) tendría ancho cero y las líneas SD quedarían en un solo punto.
  if (puntos.length < 2 || puntos[0]!.x === puntos[puntos.length - 1]!.x) return undefined;
  return armarCurvaDesdePuntos(puntos, dataset, c.idSerie, 'Peso', 'kg', LINEAS_SD, PASO_MUESTREO_CM);
}

type ObsPaciente = readonly Observation[];
type PacienteCurva = Pick<Patient, 'birthDate' | 'gender'>;
/** Pares peso/longitud de 0 a 24 meses (028B): x = longitud en cm, y = peso en kg. */
export const puntosPesoLongitud = (o: ObsPaciente, p: PacienteCurva): PuntoSerie[] => puntosPesoParaTalla(PESO_PARA_LONGITUD, o, p);
export const curvaPesoLongitudDe = (o: ObsPaciente, p: PacienteCurva): CurvaPercentilDatos | undefined => curvaPesoParaTallaDe(PESO_PARA_LONGITUD, o, p);
/** Pares peso/talla de 24 a 60 meses (028B): x = talla en cm, y = peso en kg. */
export const puntosPesoTalla = (o: ObsPaciente, p: PacienteCurva): PuntoSerie[] => puntosPesoParaTalla(PESO_PARA_TALLA, o, p);
export const curvaPesoTallaDe = (o: ObsPaciente, p: PacienteCurva): CurvaPercentilDatos | undefined => curvaPesoParaTallaDe(PESO_PARA_TALLA, o, p);

/** Rango de edad postmenstrual que cubre Fenton 2003 — 22.43 a 50.43 semanas gestacionales, en días (028A-1). */
const RANGO_FENTON = { min: 22.42857143 * 7, max: 50.42857143 * 7 };

/** Puntos {x: edad postmenstrual en días, y: valor} de una medida de Fenton (por LOINC), sin mínimo de puntos. Exige la edad gestacional al nacer en el `component` de la Observation. `[]` si falta la fecha de nacimiento o el sexo. */
export function puntosFenton(loinc: string, observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): PuntoSerie[] {
  if (!patient.birthDate) return [];
  if (patient.gender !== 'male' && patient.gender !== 'female') return [];
  const nacimiento = patient.birthDate;

  const puntos: PuntoSerie[] = [];
  for (const o of observaciones) {
    if (!o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === loinc)) continue;
    if (typeof o.effectiveDateTime !== 'string' || typeof o.valueQuantity?.value !== 'number') continue;
    const semanasAlNacer = edadGestacionalAlNacerDe(o);
    if (semanasAlNacer === undefined) continue;
    const edadPostmenstrualDias = semanasAlNacer * 7 + edadExactaEnDias(nacimiento, o.effectiveDateTime);
    if (edadPostmenstrualDias < RANGO_FENTON.min || edadPostmenstrualDias > RANGO_FENTON.max) continue;
    puntos.push({ x: edadPostmenstrualDias, y: o.valueQuantity.value });
  }
  return puntos.sort((a, b) => a.x - b.x);
}

/**
 * 028A-1 (Fenton, prematuros) — a diferencia de todo lo demás de la Serie
 * 028, el eje X es la edad POSTMENSTRUAL (edad gestacional al nacer + edad
 * cronológica desde el nacimiento), no la edad cronológica ni la corregida.
 * Exige el `component` de edad gestacional al nacer en la propia Observation
 * (sin él no hay eje X posible) — `crearFormularioCurvaCrecimiento` con
 * `incluirEdadGestacionalAlNacer: 'requerido'` lo garantiza en 028A-1.
 */
function crearCurvaFenton(
  loinc: string,
  datasetBoys: readonly FilaLmsTupla[],
  datasetGirls: readonly FilaLmsTupla[],
  idSerie: string,
  etiquetaSerie: string,
  unidad: string,
): (observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>) => CurvaPercentilDatos | undefined {
  return function curvaDe(observaciones, patient) {
    if (!patient.birthDate) return undefined;
    if (patient.gender !== 'male' && patient.gender !== 'female') return undefined;
    const puntos = puntosFenton(loinc, observaciones, patient);
    const dataset = patient.gender === 'male' ? datasetBoys : datasetGirls;
    return armarCurvaDesdePuntos(puntos, dataset, idSerie, etiquetaSerie, unidad);
  };
}

export const curvaPesoFentonDe = crearCurvaFenton(PESO_LOINC, comoFilasLms(datosPesoFenton.boys), comoFilasLms(datosPesoFenton.girls), 'peso-fenton-real', 'Peso', 'kg');
export const curvaLongitudFentonDe = crearCurvaFenton(TALLA_LOINC, comoFilasLms(datosLongitudFenton.boys), comoFilasLms(datosLongitudFenton.girls), 'longitud-fenton-real', 'Longitud', 'cm');
export const curvaPerimetroCefalicoFentonDe = crearCurvaFenton(
  PERIMETRO_CEFALICO_LOINC,
  comoFilasLms(datosPerimetroCefalicoFenton.boys),
  comoFilasLms(datosPerimetroCefalicoFenton.girls),
  'perimetro-cefalico-fenton-real',
  'Perímetro cefálico',
  'cm',
);

/** Referencia de una gráfica del PDF firmado de los controles de niño (016B/016C/028A-1 Fenton, 028B OMS 0-5 años, 028C OMS 5-19 años): ver `lineasReferenciaControlNino`. */
export type ReferenciaControlNino = 'fenton-peso' | 'fenton-longitud' | 'fenton-perimetro-cefalico' | 'oms0a5-peso' | 'oms0a5-talla' | 'oms0a5-perimetro-cefalico' | 'oms0a5-peso-longitud' | 'oms0a5-peso-talla' | 'oms0a5-imc' | 'oms5a19-peso' | 'oms5a19-talla' | 'oms5a19-imc';

interface TramoReferenciaControlNino {
  readonly datos: { readonly boys: readonly (readonly number[])[]; readonly girls: readonly (readonly number[])[] };
  /** Rango y paso del muestreo en la unidad del eje X del dataset: días (casi todos) o cm (peso para la longitud/talla). */
  readonly minDias: number;
  readonly maxDias: number;
  readonly pasoDias: number;
}

const REFERENCIAS_CONTROL_NINO: Record<ReferenciaControlNino, TramoReferenciaControlNino> = {
  // Fenton: de 22.43 a 50 semanas (la lámina impresa llega a 50), una fila por semana en el dataset. OJO: en `datos*Fenton.json` `girls` es hoy copia de `boys` (limitación conocida).
  'fenton-peso': { datos: datosPesoFenton, minDias: RANGO_FENTON.min, maxDias: 50 * 7, pasoDias: 7 },
  'fenton-longitud': { datos: datosLongitudFenton, minDias: RANGO_FENTON.min, maxDias: 50 * 7, pasoDias: 7 },
  'fenton-perimetro-cefalico': { datos: datosPerimetroCefalicoFenton, minDias: RANGO_FENTON.min, maxDias: 50 * 7, pasoDias: 7 },
  // OMS 0-5 años: de 0 a 60 meses, un punto por mes.
  'oms0a5-peso': { datos: datosPesoEdad, minDias: 0, maxDias: 60 * DIAS_POR_MES, pasoDias: DIAS_POR_MES },
  'oms0a5-talla': { datos: datosTallaEdad, minDias: 0, maxDias: 60 * DIAS_POR_MES, pasoDias: DIAS_POR_MES },
  'oms0a5-perimetro-cefalico': { datos: datosPerimetroCefalico, minDias: 0, maxDias: 60 * DIAS_POR_MES, pasoDias: DIAS_POR_MES },
  // OMS 0-5 años, curvas cuyo eje X no es solo la edad: peso para la longitud (45-110 cm) y peso para la talla (65-120 cm), un punto por cm; IMC/edad, un punto por mes.
  'oms0a5-peso-longitud': { datos: datosPesoLongitud, minDias: 45, maxDias: 110, pasoDias: PASO_MUESTREO_CM },
  'oms0a5-peso-talla': { datos: datosPesoTalla, minDias: 65, maxDias: 120, pasoDias: PASO_MUESTREO_CM },
  'oms0a5-imc': { datos: datosImc0a5, minDias: 0, maxDias: 60 * DIAS_POR_MES, pasoDias: DIAS_POR_MES },
  // OMS 5-19 años: el dataset de peso/edad termina a los 10 años (tope oficial de la OMS); talla e IMC llegan a los 19.
  'oms5a19-peso': { datos: datosPeso5a10, minDias: RANGO_5A19.min + 1, maxDias: 3652.5, pasoDias: 60 },
  'oms5a19-talla': { datos: datosTalla5a19, minDias: RANGO_5A19.min + 1, maxDias: 6939.75, pasoDias: 120 },
  'oms5a19-imc': { datos: datosImc5a19, minDias: RANGO_5A19.min + 1, maxDias: 6939.75, pasoDias: 120 },
};

/**
 * Líneas de referencia (7 desviaciones estándar, -3 a +3) de una gráfica de los controles de niño para el PDF firmado, muestreadas para no pasar de
 * 80 puntos por línea (tope del servidor). `[]` si el sexo no es male/female. Mismos datasets que la pantalla (`SignosVitalesTab`).
 */
export function lineasReferenciaControlNino(gender: Patient['gender'], referencia: ReferenciaControlNino): Serie[] {
  if (gender !== 'male' && gender !== 'female') return [];
  const t = REFERENCIAS_CONTROL_NINO[referencia];
  const dataset = comoFilasLms(gender === 'male' ? t.datos.boys : t.datos.girls);
  return LINEAS_SD.map((linea) => lineaSd(dataset, linea, t.minDias, t.maxDias, t.pasoDias));
}

/** Edad postmenstrual en días -> texto legible ("32 sem") para el eje X del gráfico de Fenton (028A-1). */
export function formatoEdadGestacionalDesdeDias(dias: number): string {
  return `${Math.round(dias / 7)} sem`;
}

/** Edad en días -> texto legible ("6 m", "2 a 3 m") para el eje X / tooltip. */
export function formatoEdadDesdeDias(dias: number): string {
  const meses = Math.round(dias / 30.4375);
  if (meses < 24) return `${meses} m`;
  const anios = Math.floor(meses / 12);
  const mesesRestantes = meses % 12;
  return mesesRestantes === 0 ? `${anios} a` : `${anios} a ${mesesRestantes} m`;
}
